<!-- 
  Laydown Log Component
  ---------------------
  This component handles the display and interaction with the laydown space logs. It includes a table to display the logs,
  modals for filtering logs and viewing/editing log details, and a date picker modal for selecting dates.

  Features:
  - Location-aware: Conditionally renders based on whether selected location supports laydown space.
    - Shows warning card with available locations when laydown not supported.
    - Shows table and modals when laydown is supported at current location.
  - Displays logs in a table with pagination and sorting.
  - Provides a filter modal to filter logs based on various criteria.
  - Allows viewing and editing log details in a modal.
  - Includes a date picker modal for selecting dates.

  Note: This component does NOT include photo management or email functionality (unlike ReceivingLogComponent).
-->
<div class="laydown-log-page-container">
  <!-- Show guard component if laydown not available -->
  <app-laydown-location-guard *ngIf="!selectedLocation.laydownSpaceAvailable"></app-laydown-location-guard>
  
  <!-- Only show table if laydown is available -->
  <ng-container *ngIf="selectedLocation.laydownSpaceAvailable">
    <!-- Table Container -->
    <div class="table-container">
      <!-- Table Component to display logs -->
      <app-table
      [items]="laydownLogs"
      [totalPages]="totalPageCount"
      [columns]="columnData"
      [dataLoading]="logsLoading"
      [tableConfig]="tableConfig"
      [user]="user"
      [isArchiveTable]="false"
      (sortByColumn)="onSortByColumn($event)"
      (sortColumnDirection)="onSortByColumnDirection($event)"
      (updatePageNumber)="onUpdatePageNumber($event)"
      (openRowModal)="onOpenRowModal($event)"
      (openFilterModal)="onOpenFilterModal()"
      (navToPage)="onNavToNewLogPage()"
      (deleteRow)="onDeleteLogRow($event)"
      [isLargeScreen]="isLargeScreen"
    ></app-table>
  </div>

  <!-- Modal for filtering logs -->
  <ion-modal
    class="card-popup"
    [isOpen]="isFilterModalOpen"
    (didDismiss)="closeFilterModal()"
    [backdropDismiss]="true"
  >
    <ng-template>
      <!--Modal header with exit button-->
      <ion-header>
        <ion-toolbar>
          <ion-title>Filter Options</ion-title>
          <ion-buttons slot="end">
            <ion-button (click)="closeFilterModal()"
              ><ion-icon name="close" slot="start"></ion-icon>
              Close</ion-button
            >
          </ion-buttons>
        </ion-toolbar>
      </ion-header>

      <!-- Buttons To clear or apply the selected form filters -->
      <div class="modal-actions-header">
        <ion-button expand="full" type="submit" (click)="onSubmitFilters()">
          Apply Filters
        </ion-button>
        <ion-button expand="full" color="medium" (click)="onClearFilters()">
          Clear All
        </ion-button>
      </div>

      <!-- Filter Options -->
      <ion-content>
        <!-- Checkbox list for additional filter options -->
        <ion-list class="checkbox-list" lines="none">
          <ion-item>
            <!--Checkbox to toggle viewing logs with date out-->
            <ion-checkbox
              labelPlacement="end"
              justify="start"
              [checked]="showLogsWithDateOut"
              (ionChange)="onToggleLogsWithDateOut()"
            >
              Include Entries with "Date Out"
            </ion-checkbox>
          </ion-item>
        
        </ion-list>

        <!-- Form for filtering logs based on column data -->
        <form [formGroup]="filterForm">
          <ion-list>
            <ion-list-header>
              <ion-note style="font-size: 18px">
                Filter table by column values:
              </ion-note>
            </ion-list-header>

            <ion-item>
              <!-- Input field for general search -->
              <ion-input
                formControlName="_ALL"
                placeholder="Search all fields..."
                type="text"
                [labelPlacement]="filterLogsLabelPlacement"
                label="General Search"
                [clearInput]="true"
              >
              </ion-input>
            </ion-item>

            <!-- Iterate over column data to create filter inputs -->
            <ion-item *ngFor="let column of columnData">
              <!-- Input field for non-date columns -->
              <ion-input
                *ngIf="column.formFieldType !== 'date'"
                [formControlName]="column.name"
                [placeholder]="
                  column.placeholder
                    ? column.placeholder
                    : 'Filter by ' + column.label
                "
                type="text"
                [labelPlacement]="filterLogsLabelPlacement"
                [label]="column.label"
                [clearInput]="true"
              >
              </ion-input>

              <!-- Input field for date columns. 
                   These will open the date modal when clicked into -->
              <ion-input
                *ngIf="column.formFieldType === 'date'"
                [formControlName]="column.name"
                [placeholder]="
                  column.placeholder
                    ? column.placeholder
                    : 'Filter by ' + column.label
                "
                [labelPlacement]="editLogLabelPlacement"
                [label]="column.label"
                [disabled]="column.disabled ? column.disabled : false"
                [clearInput]="true"
                (click)="onOpenDateModal(column.name, filterForm)"
              >
              </ion-input>
            </ion-item>
          </ion-list>
        </form>
      </ion-content>
    </ng-template>
  </ion-modal>

  <!-- Modal to Show Item Details -->
  <ion-modal
    class="card-popup"
    [isOpen]="isRowModalOpen"
    (didDismiss)="closeRowModal()"
    [backdropDismiss]="true"
  >
    <ng-template>
      <!-- Modal Header with toolbar and action buttons -->
      <ion-header>
        <ion-toolbar>
          <!-- Button to open item details in a separate window -->
          <ion-buttons slot="start">
            <ion-button
              fill="clear"
              title="Open Log Details in New Window"
              (click)="openRowDetailsWindow()"
            >
              <ion-icon name="open"></ion-icon>
            </ion-button>
          </ion-buttons>

          <!-- Modal Title -->
          <ion-title slot="start" style="padding-left: 0">
           Entry Details
          </ion-title>

          <!-- Action buttons: Update and Close -->
          <ion-buttons slot="end">
            <!-- Update Button -->
            <ion-button
              type="submit"
              (click)="updateLogRow()"
         
              class="submit-btn"
            >
              <ion-icon name="checkmark"></ion-icon>&nbsp;
              Update Entry
            </ion-button>
            
            <!-- Close Button -->
            <ion-button (click)="closeRowModal()">
              <ion-icon name="close" slot="start"></ion-icon>
              Close
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>

      <!-- Form to edit log details -->
      <ion-content>
        <form [formGroup]="editLogForm">
          <ion-grid>
            <!-- Row 1: Job Number -->
            <ion-row>
              <ion-col size="12">
                <ion-item class="detail-item">
                  <ion-input
                    formControlName="jobNumber"
                    placeholder="Enter job number"
                    type="text"
                    [labelPlacement]="editLogLabelPlacement"
                    label="Job Number"
                    [clearInput]="true"
                    [errorText]="getErrorText('jobNumber', 'Job Number')"
                  >
                  </ion-input>
                </ion-item>
              </ion-col>
            </ion-row>

            <!-- Row 2: Contact -->
            <ion-row>
              <ion-col size="12">
                <ion-item class="detail-item">
                  <ion-input
                    #contactInput
                    formControlName="contact"
                    placeholder="Enter contact"
                    type="email"
                    [labelPlacement]="editLogLabelPlacement"
                    label="Contact"
                    [clearInput]="true"
                    [errorText]="getErrorText('contact', 'Contact')"
                  >
                  </ion-input>
                </ion-item>
                
                <!-- Autocomplete dropdown positioned outside ion-item -->
                <app-autocomplete-dropdown
                  *ngIf="contactInputElement"
                  [options]="employeeEmails"
                  [formGroup]="editLogForm"
                  controlName="contact"
                  [inputElement]="contactInputElement"
                >
                </app-autocomplete-dropdown>
              </ion-col>
            </ion-row>

            <!-- Row 3: Space -->
            <ion-row>
              <ion-col size="12">
                <ion-item class="detail-item">
                  <ion-select
                    aria-label="Space"
                    interface="popover"
                    formControlName="space"
                    placeholder="Select space"
                    [labelPlacement]="editLogLabelPlacement"
                    label="Space"
                    [disabled]="!userIsAdmin"
                  >
                    <ion-select-option value="A">A</ion-select-option>
                    <ion-select-option value="B">B</ion-select-option>
                  </ion-select>
                </ion-item>
              </ion-col>
            </ion-row>

            <!-- Row 4: Date In -->
            <ion-row>
              <ion-col size="12">
                <ion-item class="detail-item">
                  <ion-input
                    formControlName="dateIn"
                    placeholder="MM/DD/YY"
                    [labelPlacement]="editLogLabelPlacement"
                    label="Date In"
                    [clearInput]="true"
                    [disabled]="!userIsAdmin"
                    [errorText]="getErrorText('dateIn', 'Date In')"
                    (click)="onOpenDateModal('dateIn', editLogForm)"
                  >
                  </ion-input>
                </ion-item>
              </ion-col>
            </ion-row>

            <!-- Row 5: Est Date Out -->
            <ion-row>
              <ion-col size="12">
                <ion-item class="detail-item">
                  <ion-input
                    formControlName="estDateOut"
                    placeholder="MM/DD/YY"
                    [labelPlacement]="editLogLabelPlacement"
                    label="Est Date Out"
                    [clearInput]="true"
                    [disabled]="!userIsAdmin"
                    [errorText]="getErrorText('estDateOut', 'Est Date Out')"
                    (click)="onOpenDateModal('estDateOut', editLogForm)"
                  >
                  </ion-input>
                </ion-item>
              </ion-col>
            </ion-row>

            <!-- Row 6: Date Out -->
            <ion-row>
              <ion-col size="12">
                <ion-item class="detail-item">
                  <ion-input
                    formControlName="dateOut"
                    placeholder="MM/DD/YY"
                    [labelPlacement]="editLogLabelPlacement"
                    label="Date Out"
                    [clearInput]="true"
                    [errorText]="getErrorText('dateOut', 'Date Out')"
                    (click)="onOpenDateModal('dateOut', editLogForm)"
                  >
                  </ion-input>
                </ion-item>
              </ion-col>
            </ion-row>

            <!-- Row 7: Logged By -->
            <ion-row>
              <ion-col size="12">
                <ion-item  class="detail-item">
                  <ion-input
                    formControlName="loggedBy"
                    type="text"
                    [labelPlacement]="editLogLabelPlacement"
                    label="Logged By"
                    [disabled]="true"
                    [errorText]="getErrorText('loggedBy', 'Logged By')"
                  >
                  </ion-input>
                </ion-item>
              </ion-col>
            </ion-row>

            <!-- Row 8: Storage Facility -->
            <ion-row>
              <ion-col size="12">
                <ion-item class="detail-item">
                  <ion-select
                    aria-label="Storage Facility"
                    interface="popover"
                    formControlName="storageFacility"
                    placeholder="Select storage facility"
                    [labelPlacement]="editLogLabelPlacement"
                    label="Storage Facility"
                  >
                    <ion-select-option
                      *ngFor="let option of columnData[7]?.options"
                      [value]="option.value"
                    >
                      {{ option.label }}
                    </ion-select-option>
                  </ion-select>
                </ion-item>
              </ion-col>
            </ion-row>
          </ion-grid>
        </form>
      </ion-content>
    </ng-template>
  </ion-modal>

  <!-- Modal to Show datepicker -->
  <ion-modal
    class="date-popup"
    [isOpen]="isDateModalOpen"
    (didDismiss)="closeDateModal()"
  >
    <ng-template>
      <!-- Custom Date Picker Component -->
      <app-date-picker
        [startDate]="tempSelectedDate"
        (dateSelected)="updateSelectedDate($event)"
        (cancelDatePicker)="cancelDateSelection()"
        (confirmSelectedDate)="closeDateModal()"
      ></app-date-picker>
    </ng-template>
  </ion-modal>
  </ng-container>
</div>